/**
 * NotificationBell
 * Polls the server notification feed, shows an unread badge, a dropdown list,
 * and fires desktop notifications (browser Notification API) for new events
 * while the dashboard is open.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ShoppingCart, MapPin, Fuel, AlertOctagon, Check, CalendarCheck } from 'lucide-react';
import { systemAPI } from '../api/client';
import { formatIstDateTime } from '../utils/formatIst';
import { adminAssetUrl } from '../utils/routerBasename';
import {
  getNotificationRoute,
  routeFromAbsoluteUrl,
} from '../utils/notificationLinks';

const POLL_MS = 25000;
const SEEN_KEY = 'qtf_notifications_seen_id';

const pushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof Notification !== 'undefined';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const registerPush = async () => {
  if (!pushSupported()) throw new Error('Push not supported in this browser');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Permission denied');
  const reg = await navigator.serviceWorker.register(adminAssetUrl('/sw.js'));
  await navigator.serviceWorker.ready;
  const { data } = await systemAPI.getPushKey();
  const key = data?.key;
  if (!key) throw new Error('Server push key unavailable');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  await systemAPI.subscribePush(sub);
  return true;
};

const TYPE_META = {
  order: { icon: ShoppingCart, color: 'text-blue-600 bg-blue-50' },
  visit: { icon: MapPin, color: 'text-green-600 bg-green-50' },
  fuel: { icon: Fuel, color: 'text-amber-600 bg-amber-50' },
  leave: { icon: CalendarCheck, color: 'text-purple-600 bg-purple-50' },
  critical: { icon: AlertOctagon, color: 'text-red-600 bg-red-50' },
};

const getSeenId = () => {
  const v = parseInt(localStorage.getItem(SEEN_KEY), 10);
  return Number.isFinite(v) ? v : 0;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [latestId, setLatestId] = useState(getSeenId());
  const [seenId, setSeenId] = useState(getSeenId());
  const [desktopEnabled, setDesktopEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const dropdownRef = useRef(null);
  const initialized = useRef(false);

  const unreadCount = items.filter((n) => n.id > seenId).length;

  const fireDesktop = useCallback((newItems) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Only notify for the few newest to avoid spamming on first load.
    newItems.slice(0, 3).forEach((n) => {
      try {
        const route = getNotificationRoute(n);
        const note = new Notification(n.title, {
          body: n.message || '',
          tag: `qtf-${n.id}`,
        });
        if (route) {
          note.onclick = () => {
            window.focus();
            navigate(route);
            note.close();
          };
        }
      } catch {
        /* ignore */
      }
    });
  }, [navigate]);

  const markAllRead = useCallback(() => {
    if (latestId > 0) {
      localStorage.setItem(SEEN_KEY, String(latestId));
      setSeenId(latestId);
    }
  }, [latestId]);

  const openNotification = useCallback((notification) => {
    const route = getNotificationRoute(notification);
    setOpen(false);
    markAllRead();
    if (route) navigate(route);
  }, [navigate, markAllRead]);

  const poll = useCallback(async () => {
    try {
      const res = await systemAPI.getNotifications(0, 30);
      const data = res.data?.data || [];
      const serverLatest = res.data?.latestId || 0;

      // Detect genuinely new items (since last poll) for desktop pop-ups.
      if (initialized.current) {
        const fresh = data.filter((n) => n.id > latestId);
        if (fresh.length) fireDesktop(fresh);
      }
      initialized.current = true;

      setItems(data);
      setLatestId(serverLatest);
    } catch {
      /* network hiccup — keep prior items */
    }
  }, [latestId, fireDesktop]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background push click → navigate inside the open dashboard tab.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type !== 'NOTIFICATION_NAV' || !event.data.url) return;
      const route = routeFromAbsoluteUrl(event.data.url);
      if (route) navigate(route);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Opening the panel marks current items as seen.
      setTimeout(markAllRead, 1200);
    }
  };

  const enableDesktop = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setDesktopEnabled(perm === 'granted');
  };

  // Reflect existing push subscription state on mount.
  useEffect(() => {
    if (!pushSupported()) return;
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg && reg.pushManager.getSubscription())
      .then((sub) => setPushEnabled(!!sub))
      .catch(() => {});
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await registerPush();
      setPushEnabled(true);
      setDesktopEnabled(true);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Could not enable background push: ${e.message}`);
    } finally {
      setPushBusy(false);
    }
  };

  const linkLabel = (type) => {
    if (type === 'leave') return 'leave requests';
    if (type === 'order') return 'orders';
    if (type === 'visit') return 'visit logs';
    if (type === 'fuel') return 'fuel expenses';
    if (type === 'critical') return 'system logs';
    return 'details';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleOpen}
        className="p-2.5 rounded-xl hover:bg-slate-100 text-slate-600 relative transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-slate-800">Notifications</span>
            <button
              onClick={markAllRead}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
            >
              <Check className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>

          {!desktopEnabled && typeof Notification !== 'undefined' && (
            <button
              onClick={enableDesktop}
              className="w-full text-left px-4 py-2 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              Enable desktop pop-up notifications
            </button>
          )}

          {pushSupported() && !pushEnabled && (
            <button
              onClick={enablePush}
              disabled={pushBusy}
              className="w-full text-left px-4 py-2 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60"
            >
              {pushBusy ? 'Enabling…' : 'Enable background push (alerts even when tab is closed)'}
            </button>
          )}
          {pushSupported() && pushEnabled && (
            <div className="w-full px-4 py-2 text-xs text-emerald-700 bg-emerald-50 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Background push enabled
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">No notifications yet</div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || {
                  icon: Bell,
                  color: 'text-slate-600 bg-slate-100',
                };
                const Icon = meta.icon;
                const unread = n.id > seenId;
                const route = getNotificationRoute(n);
                return (
                  <button
                    type="button"
                    key={n.id}
                    onClick={() => openNotification(n)}
                    disabled={!route}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 transition-colors ${
                      unread ? 'bg-blue-50/40' : ''
                    } ${route ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className={`p-2 rounded-lg h-fit ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800">{n.title}</div>
                      {n.message && (
                        <div className="text-xs text-slate-500 break-words">{n.message}</div>
                      )}
                      {route && (
                        <div className="text-[10px] font-semibold text-blue-600 mt-1">
                          Open {linkLabel(n.type)} →
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {formatTs(n.created_at)}
                      </div>
                    </div>
                    {unread && <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function formatTs(ts) {
  if (!ts) return '';
  try {
    return formatIstDateTime(ts);
  } catch {
    return ts;
  }
}

export default NotificationBell;
